import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '@/providers/AuthProvider';
import { consoleApi } from '@/lib/consoleApi';
import { peekSignOutReason, clearSignOutReason } from '@/lib/authExpiry';
import { roleSatisfies } from '@/lib/roleRank';
import { CodeSignIn } from '@/components/CodeSignIn';
import { BioweaveAuthError } from '@bioweave/api-client/errors';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

export function SignInPage() {
  const navigate = useNavigate();
  const { signIn, token, isReady, cognitoEnabled, unlinked } = useAuth();
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Pure read every render — the notice stands until a successful sign-in
  // clears it, which is the event that actually resolves the expiry. Reading
  // and clearing here instead would be a side effect in render.
  const expired = peekSignOutReason() === 'expired';

  if (isReady && token) return <Navigate to="/" replace />;

  const handleVerify = async () => {
    const trimmed = value.trim();
    if (!trimmed) return;
    setBusy(true); setError(null);
    try {
      const who = await consoleApi.authVerify(trimmed);
      clearSignOutReason();
      signIn({ token: trimmed, userRef: who.user_ref, role: who.role });
      navigate(roleSatisfies(who.role, 'advisor') ? '/' : '/not-authorised', { replace: true });
    } catch (e) {
      setError(e instanceof BioweaveAuthError
        ? 'That token was rejected.'
        : 'Could not reach the backend. Is VITE_BIOWEAVE_API_BASE_URL set and core up?');
    } finally { setBusy(false); }
  };

  return (
    <div className="mx-auto flex min-h-screen max-w-sm flex-col justify-center gap-4 p-21">
      <h1 className="text-2xl font-semibold">Bioweave Console</h1>
      {expired ? (
        <p data-testid="session-expired" className="rounded border-l-2 border-flagged bg-surface-sunken p-13 text-sm text-text-primary">
          Your session ended — the token was expired, revoked or rotated. Sign in with a current one.
        </p>
      ) : null}
      {/* Copy approved by the owner, 2026-10-19 (the same strings as the app's auth.signIn.code). */}
      {unlinked ? (
        <p data-testid="signin-unlinked" className="rounded border-l-2 border-flagged bg-surface-sunken p-13 text-sm text-text-primary">
          You are signed in, but this email is not linked to a Bioweave account yet.
        </p>
      ) : null}
      {cognitoEnabled ? (
        <>
          <p className="text-sm text-text-secondary">Enter your email and we will send you a sign-in code.</p>
          <CodeSignIn />
          {/* The pasted token stays until the cutover: old tokens are still accepted. */}
          <p className="pt-8 text-sm text-text-secondary">Or sign in with an access token.</p>
        </>
      ) : (
        <p className="text-sm text-text-secondary">Enter your advisor access token.</p>
      )}
      <Input
        data-testid="token-input"
        type="password" autoComplete="off" value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') void handleVerify(); }}
        placeholder="Paste your bearer token"
      />
      {error ? <p className="text-sm text-flagged">{error}</p> : null}
      <Button
        data-testid="signin-btn"
        variant={cognitoEnabled ? 'outline' : undefined}
        disabled={busy || value.trim().length === 0}
        onClick={() => void handleVerify()}
      >{busy ? 'Verifying…' : 'Sign in'}</Button>
    </div>
  );
}
